import type React from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useState, useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select"
import { Textarea } from "../components/ui/textarea"
import { Badge } from "../components/ui/badge"
import { Alert, AlertDescription } from "../components/ui/alert"
import { Loader2, Plus, X, CheckCircle, AlertCircle } from "lucide-react"
import { getDevice, updateDevice,deleteDevice } from "../api/axios"

interface UpdateDeviceFormData {
  name: string
  hostname: string
  ipAddress: string
  manufacturer?: string
  type: string
  description?: string
  location?: string
  group?: string
  status?: string
  tags: string[]
  snmp?: Array<{
    name: string
    version: string
    port: number
    community?: string
    username?: string
    authentication_protocol?: string
    authentication_password?: string
    encryption?: string
    encryption_password?: string
    oid?: string
  }>
  ssh?: Array<{
    name: string
    host: string
    port: number
    username: string
    enc_password?: string
    private_key?: string
    command?: string
  }>
  metadata?: any
  maintenanceMode?: boolean
  maintenanceNotes?: string
  maintenanceEndTime?: string
  managementUrl?: string
}

interface UpdateDeviceFormProps {
  deviceId?: string;
  onClose?: () => void;
}

export function UpdateDeviceForm(props?: UpdateDeviceFormProps) {
  const { deviceId, onClose } = props || {}
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const id = deviceId || params.id
  const [protocol, setProtocol] = useState<"snmp" | "ssh">("snmp")
  const [formData, setFormData] = useState<UpdateDeviceFormData>({
    name: "",
    hostname: "",
    ipAddress: "",
    manufacturer: "",
    type: "server",
    description: "",
    location: "",
    group: "Default",
    status: "unknown",
    tags: [],
    snmp: [],
    ssh: [],
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle")
  const [tagInput, setTagInput] = useState("")

  // Fetch device data
  const { data: device, isLoading, error } = useQuery({
    queryKey: ["device", id],
    queryFn: () => getDevice(id!),
    enabled: !!id,
  })

  //Delete Device
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState("")

  const handleDelete = async () => {
  if (!id) return
  setIsDeleting(true)
  setDeleteError("")
  try {
    await deleteDevice(id)
    setSubmitStatus("success") // Show success alert
    // Wait for 1.5 seconds to show the success message
    setTimeout(() => {
      if (onClose) {
        onClose()
      } else {
        navigate("/manage-devices") // Navigate to manage-device page
        window.location.reload() // Refresh the page after navigation
      }
    }, 1500)
  } catch (err) {
    setDeleteError("Failed to delete device. Please try again.")
  } finally {
    setIsDeleting(false)
  }
}



  // Populate form when device data is loaded
  useEffect(() => {
    if (device) {
      setFormData({
        name: device.name || "",
        hostname: device.hostname || "",
        ipAddress: device.ipAddress || "",
        manufacturer: device.manufacturer || "",
        type: device.type || "server",
        description: device.description || "",
        location: device.location || "",
        group: device.group || "Default",
        status: device.status || "unknown",
        tags: device.tags || [],
        snmp: device.snmp || [],
        ssh: device.ssh || [],
        metadata: device.metadata || {},
        maintenanceMode: device.maintenanceMode || false,
        maintenanceNotes: device.maintenanceNotes || "",
        maintenanceEndTime: device.maintenanceEndTime || "",
        managementUrl: device.managementUrl || "",
      })

      // Set protocol based on existing configuration
      if (device.snmp && device.snmp.length > 0) {
        setProtocol("snmp")
      } else if (device.ssh && device.ssh.length > 0) {
        setProtocol("ssh")
      }
    }
  }, [device])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = "Device name is required"
    }

    if (!formData.hostname.trim()) {
      newErrors.hostname = "Hostname is required"
    }

    if (!formData.ipAddress.trim()) {
      newErrors.ipAddress = "IP address is required"
    }

    if (protocol === "snmp" && formData.snmp && formData.snmp.length > 0) {
      const snmp = formData.snmp[0]
      if (snmp.port && (snmp.port < 1 || snmp.port > 65535)) {
        newErrors.snmpPort = "Port must be between 1 and 65535"
      }

      if (snmp.version === "3") {
        if (!snmp.username?.trim()) {
          newErrors.snmpUsername = "Username is required for SNMP v3"
        }

        if (!snmp.authentication_password?.trim()) {
          newErrors.snmpAuthPassword = "Authentication password is required for SNMP v3"
        } else if (snmp.authentication_password.length < 8) {
          newErrors.snmpAuthPassword = "Authentication password must be at least 8 characters"
        }

        if (snmp.encryption_password && snmp.encryption_password.length < 8) {
          newErrors.snmpPrivPassword = "Privacy password must be at least 8 characters"
        }
      }
    } else if (protocol === "ssh" && formData.ssh && formData.ssh.length > 0) {
      const ssh = formData.ssh[0]
      if (!ssh.host?.trim()) newErrors.sshHost = "Host required"
      if (!ssh.port || ssh.port < 1 || ssh.port > 65535) newErrors.sshPort = "Port must be between 1 and 65535"
      if (!ssh.username?.trim()) newErrors.sshUsername = "Username required"
      if (!ssh.enc_password?.trim() && !ssh.private_key?.trim()) newErrors.sshPassword = "Password or Private Key required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) {
      setSubmitStatus("error")
      return
    }
    setIsSubmitting(true)
    setSubmitStatus("idle")

    try {
      // Prepare payload for backend
      const deviceData: any = {
        name: formData.name,
        hostname: formData.hostname,
        ipAddress: formData.ipAddress,
        type: formData.type,
        group: formData.group,
        status: formData.status,
        tags: formData.tags,
        manufacturer: formData.manufacturer,
        description: formData.description,
        location: formData.location,
        metadata: formData.metadata,
        maintenanceMode: formData.maintenanceMode,
        maintenanceNotes: formData.maintenanceNotes,
        maintenanceEndTime: formData.maintenanceEndTime,
        managementUrl: formData.managementUrl,
      }

      // Build SNMP or SSH array for backend
      if (protocol === "snmp" && formData.snmp && formData.snmp.length > 0) {
        deviceData.snmp = formData.snmp
      } else if (protocol === "ssh" && formData.ssh && formData.ssh.length > 0) {
        deviceData.ssh = formData.ssh
      }

      const response = await updateDevice(id!, deviceData)
      if (response) {
        setSubmitStatus("success")
        setTimeout(() => {
          if (onClose) {
            onClose()
          } else {
            navigate(`/device/${id}`)
          }
        }, 2000)
      } else {
        setSubmitStatus("error")
        setErrors(prev => ({ ...prev, submit: "Failed to update device. Please check your configuration and try again." }))
      }
    } catch (error: any) {
      setSubmitStatus("error")
      const backendMsg = error?.response?.data?.message || "Failed to update device. Please check your configuration and try again."
      setErrors(prev => ({ ...prev, submit: backendMsg }))
      console.error("Failed to update device:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (field: keyof UpdateDeviceFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const updated = { ...prev }
        delete updated[field]
        return updated
      })
    }
  }

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }))
      setTagInput("")
    }
  }

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter((tag) => tag !== tagToRemove) }))
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      addTag()
    }
  }

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading device information...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load device information. Please try again.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Update Device</h1>
          <p className="text-muted-foreground mt-2">Modify device configuration and settings</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Update the basic details for the device</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Device Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="e.g., Main Router"
                  className={errors.name ? "border-red-500" : ""}
                />
                {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="hostname">Hostname *</Label>
                <Input
                  id="hostname"
                  value={formData.hostname}
                  onChange={(e) => handleInputChange("hostname", e.target.value)}
                  placeholder="e.g., router.company.com"
                  className={errors.hostname ? "border-red-500" : ""}
                />
                {errors.hostname && <p className="text-sm text-red-500">{errors.hostname}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="ipAddress">IP Address *</Label>
                <Input
                  id="ipAddress"
                  value={formData.ipAddress}
                  onChange={(e) => handleInputChange("ipAddress", e.target.value)}
                  placeholder="e.g., 192.168.1.1"
                  className={errors.ipAddress ? "border-red-500" : ""}
                />
                {errors.ipAddress && <p className="text-sm text-red-500">{errors.ipAddress}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="manufacturer">Manufacturer</Label>
                <Input
                  id="manufacturer"
                  value={formData.manufacturer}
                  onChange={(e) => handleInputChange("manufacturer", e.target.value)}
                  placeholder="e.g., Palo Alto, Cisco"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Device Type</Label>
                <Select value={formData.type} onValueChange={(value) => handleInputChange("type", value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="router">Router</SelectItem>
                    <SelectItem value="switch">Switch</SelectItem>
                    <SelectItem value="server">Server</SelectItem>
                    <SelectItem value="firewall">Firewall</SelectItem>
                    <SelectItem value="access-point">Access Point</SelectItem>
                    <SelectItem value="printer">Printer</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => handleInputChange("location", e.target.value)}
                  placeholder="e.g., Server Room A"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="group">Group</Label>
                <Input
                  id="group"
                  value={formData.group}
                  onChange={(e) => handleInputChange("group", e.target.value)}
                  placeholder="e.g., Default, Core, Edge"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => handleInputChange("status", value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="up">Up</SelectItem>
                    <SelectItem value="down">Down</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="managementUrl">Management URL</Label>
                <Input
                  id="managementUrl"
                  value={formData.managementUrl}
                  onChange={(e) => handleInputChange("managementUrl", e.target.value)}
                  placeholder="e.g., https://192.168.1.1"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                placeholder="Optional description of the device..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Add tags..."
                />
                <Button type="button" onClick={addTag} size="icon" variant="outline">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)} className="ml-1 hover:text-red-500">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Maintenance Information */}
        <Card>
          <CardHeader>
            <CardTitle>Maintenance Information</CardTitle>
            <CardDescription>Configure maintenance mode and notes</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="maintenanceMode"
                checked={formData.maintenanceMode}
                onChange={(e) => handleInputChange("maintenanceMode", e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="maintenanceMode">Enable Maintenance Mode</Label>
            </div>

            {formData.maintenanceMode && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="maintenanceNotes">Maintenance Notes</Label>
                  <Textarea
                    id="maintenanceNotes"
                    value={formData.maintenanceNotes}
                    onChange={(e) => handleInputChange("maintenanceNotes", e.target.value)}
                    placeholder="Describe the maintenance work..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maintenanceEndTime">Maintenance End Time</Label>
                  <Input
                    id="maintenanceEndTime"
                    type="datetime-local"
                    value={formData.maintenanceEndTime}
                    onChange={(e) => handleInputChange("maintenanceEndTime", e.target.value)}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* SNMP/SSH Protocol Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Connection Protocol</CardTitle>
            <CardDescription>Select how you want to connect to this device</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Button 
                type="button" 
                variant={protocol === 'snmp' ? "default" : "outline"} 
                onClick={() => setProtocol("snmp")}
              >
                SNMP
              </Button>
              <Button 
                type="button" 
                variant={protocol === 'ssh' ? "default" : "outline"} 
                onClick={() => setProtocol("ssh")}
              >
                SSH
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* SNMP Configuration */}
        {protocol === "snmp" && (
          <Card>
            <CardHeader>
              <CardTitle>SNMP Configuration</CardTitle>
              <CardDescription>Configure SNMP settings for device monitoring</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="snmpVersion">SNMP Version</Label>
                  <Select 
                    value={formData.snmp?.[0]?.version || "2c"} 
                    onValueChange={(value) => {
                      const updatedSnmp = [...(formData.snmp || [])]
                      if (updatedSnmp.length === 0) {
                        updatedSnmp.push({ name: "", version: value, port: 161 })
                      } else {
                        updatedSnmp[0].version = value
                      }
                      handleInputChange("snmp", updatedSnmp)
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">SNMP v1</SelectItem>
                      <SelectItem value="2c">SNMP v2c</SelectItem>
                      <SelectItem value="3">SNMP v3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="snmpPort">SNMP Port</Label>
                  <Input
                    id="snmpPort"
                    type="number"
                    value={formData.snmp?.[0]?.port || 161}
                    onChange={(e) => {
                      const updatedSnmp = [...(formData.snmp || [])]
                      if (updatedSnmp.length === 0) {
                        updatedSnmp.push({ name: "", version: "2c", port: parseInt(e.target.value) })
                      } else {
                        updatedSnmp[0].port = parseInt(e.target.value)
                      }
                      handleInputChange("snmp", updatedSnmp)
                    }}
                    placeholder="161"
                    className={errors.snmpPort ? "border-red-500" : ""}
                  />
                  {errors.snmpPort && <p className="text-sm text-red-500">{errors.snmpPort}</p>}
                </div>

                {(formData.snmp?.[0]?.version === "2c" || !formData.snmp?.[0]?.version) && (
                  <div className="space-y-2">
                    <Label htmlFor="snmpCommunity">Community String</Label>
                    <Input
                      id="snmpCommunity"
                      value={formData.snmp?.[0]?.community || ""}
                      onChange={(e) => {
                        const updatedSnmp = [...(formData.snmp || [])]
                        if (updatedSnmp.length === 0) {
                          updatedSnmp.push({ name: "", version: "2c", port: 161, community: e.target.value })
                        } else {
                          updatedSnmp[0].community = e.target.value
                        }
                        handleInputChange("snmp", updatedSnmp)
                      }}
                      placeholder="public"
                    />
                  </div>
                )}

                {formData.snmp?.[0]?.version === "3" && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="snmpUsername">Username</Label>
                      <Input
                        id="snmpUsername"
                        value={formData.snmp?.[0]?.username || ""}
                        onChange={(e) => {
                          const updatedSnmp = [...(formData.snmp || [])]
                          if (updatedSnmp.length === 0) {
                            updatedSnmp.push({ name: "", version: "3", port: 161, username: e.target.value })
                          } else {
                            updatedSnmp[0].username = e.target.value
                          }
                          handleInputChange("snmp", updatedSnmp)
                        }}
                        placeholder="username"
                        className={errors.snmpUsername ? "border-red-500" : ""}
                      />
                      {errors.snmpUsername && <p className="text-sm text-red-500">{errors.snmpUsername}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="snmpAuthProtocol">Authentication Protocol</Label>
                      <Select 
                        value={formData.snmp?.[0]?.authentication_protocol || "SHA"} 
                        onValueChange={(value) => {
                          const updatedSnmp = [...(formData.snmp || [])]
                          if (updatedSnmp.length === 0) {
                            updatedSnmp.push({ name: "", version: "3", port: 161, authentication_protocol: value })
                          } else {
                            updatedSnmp[0].authentication_protocol = value
                          }
                          handleInputChange("snmp", updatedSnmp)
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="MD5">MD5</SelectItem>
                          <SelectItem value="SHA">SHA</SelectItem>
                          <SelectItem value="SHA-224">SHA-224</SelectItem>
                          <SelectItem value="SHA-256">SHA-256</SelectItem>
                          <SelectItem value="SHA-384">SHA-384</SelectItem>
                          <SelectItem value="SHA-512">SHA-512</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="snmpAuthPassword">Authentication Password</Label>
                      <Input
                        id="snmpAuthPassword"
                        type="password"
                        value={formData.snmp?.[0]?.authentication_password || ""}
                        onChange={(e) => {
                          const updatedSnmp = [...(formData.snmp || [])]
                          if (updatedSnmp.length === 0) {
                            updatedSnmp.push({ name: "", version: "3", port: 161, authentication_password: e.target.value })
                          } else {
                            updatedSnmp[0].authentication_password = e.target.value
                          }
                          handleInputChange("snmp", updatedSnmp)
                        }}
                        placeholder="Authentication password"
                        className={errors.snmpAuthPassword ? "border-red-500" : ""}
                      />
                      {errors.snmpAuthPassword && <p className="text-sm text-red-500">{errors.snmpAuthPassword}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="snmpPrivProtocol">Privacy Protocol</Label>
                      <Select 
                        value={formData.snmp?.[0]?.encryption || "AES"} 
                        onValueChange={(value) => {
                          const updatedSnmp = [...(formData.snmp || [])]
                          if (updatedSnmp.length === 0) {
                            updatedSnmp.push({ name: "", version: "3", port: 161, encryption: value })
                          } else {
                            updatedSnmp[0].encryption = value
                          }
                          handleInputChange("snmp", updatedSnmp)
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="DES">DES</SelectItem>
                          <SelectItem value="3DES">3DES</SelectItem>
                          <SelectItem value="AES">AES</SelectItem>
                          <SelectItem value="AES-192">AES-192</SelectItem>
                          <SelectItem value="AES-256">AES-256</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="snmpPrivPassword">Privacy Password</Label>
                      <Input
                        id="snmpPrivPassword"
                        type="password"
                        value={formData.snmp?.[0]?.encryption_password || ""}
                        onChange={(e) => {
                          const updatedSnmp = [...(formData.snmp || [])]
                          if (updatedSnmp.length === 0) {
                            updatedSnmp.push({ name: "", version: "3", port: 161, encryption_password: e.target.value })
                          } else {
                            updatedSnmp[0].encryption_password = e.target.value
                          }
                          handleInputChange("snmp", updatedSnmp)
                        }}
                        placeholder="Privacy password (optional)"
                        className={errors.snmpPrivPassword ? "border-red-500" : ""}
                      />
                      {errors.snmpPrivPassword && <p className="text-sm text-red-500">{errors.snmpPrivPassword}</p>}
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label htmlFor="snmpOid">OID (Optional)</Label>
                  <Input
                    id="snmpOid"
                    value={formData.snmp?.[0]?.oid || ""}
                    onChange={(e) => {
                      const updatedSnmp = [...(formData.snmp || [])]
                      if (updatedSnmp.length === 0) {
                        updatedSnmp.push({ name: "", version: "2c", port: 161, oid: e.target.value })
                      } else {
                        updatedSnmp[0].oid = e.target.value
                      }
                      handleInputChange("snmp", updatedSnmp)
                    }}
                    placeholder="e.g., 1.3.6.1.2.1.1.1.0"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* SSH Configuration */}
        {protocol === "ssh" && (
          <Card>
            <CardHeader>
              <CardTitle>SSH Configuration</CardTitle>
              <CardDescription>Configure SSH settings for device monitoring</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sshHost">Host</Label>
                  <Input
                    id="sshHost"
                    value={formData.ssh?.[0]?.host || ""}
                    onChange={(e) => {
                      const updatedSsh = [...(formData.ssh || [])]
                      if (updatedSsh.length === 0) {
                        updatedSsh.push({ name: "", host: e.target.value, port: 22, username: "" })
                      } else {
                        updatedSsh[0].host = e.target.value
                      }
                      handleInputChange("ssh", updatedSsh)
                    }}
                    placeholder="Hostname or IP"
                    className={errors.sshHost ? "border-red-500" : ""}
                  />
                  {errors.sshHost && <p className="text-sm text-red-500">{errors.sshHost}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sshPort">Port</Label>
                  <Input
                    id="sshPort"
                    type="number"
                    value={formData.ssh?.[0]?.port || 22}
                    onChange={(e) => {
                      const updatedSsh = [...(formData.ssh || [])]
                      if (updatedSsh.length === 0) {
                        updatedSsh.push({ name: "", host: "", port: parseInt(e.target.value), username: "" })
                      } else {
                        updatedSsh[0].port = parseInt(e.target.value)
                      }
                      handleInputChange("ssh", updatedSsh)
                    }}
                    placeholder="22"
                    className={errors.sshPort ? "border-red-500" : ""}
                  />
                  {errors.sshPort && <p className="text-sm text-red-500">{errors.sshPort}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sshUsername">Username</Label>
                  <Input
                    id="sshUsername"
                    value={formData.ssh?.[0]?.username || ""}
                    onChange={(e) => {
                      const updatedSsh = [...(formData.ssh || [])]
                      if (updatedSsh.length === 0) {
                        updatedSsh.push({ name: "", host: "", port: 22, username: e.target.value })
                      } else {
                        updatedSsh[0].username = e.target.value
                      }
                      handleInputChange("ssh", updatedSsh)
                    }}
                    placeholder="SSH username"
                    className={errors.sshUsername ? "border-red-500" : ""}
                  />
                  {errors.sshUsername && <p className="text-sm text-red-500">{errors.sshUsername}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sshPassword">Password</Label>
                  <Input
                    id="sshPassword"
                    type="password"
                    value={formData.ssh?.[0]?.enc_password || ""}
                    onChange={(e) => {
                      const updatedSsh = [...(formData.ssh || [])]
                      if (updatedSsh.length === 0) {
                        updatedSsh.push({ name: "", host: "", port: 22, username: "", enc_password: e.target.value })
                      } else {
                        updatedSsh[0].enc_password = e.target.value
                      }
                      handleInputChange("ssh", updatedSsh)
                    }}
                    placeholder="SSH password"
                    className={errors.sshPassword ? "border-red-500" : ""}
                  />
                  {errors.sshPassword && <p className="text-sm text-red-500">{errors.sshPassword}</p>}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="sshPrivateKey">Private Key (Alternative to password)</Label>
                  <Textarea
                    id="sshPrivateKey"
                    value={formData.ssh?.[0]?.private_key || ""}
                    onChange={(e) => {
                      const updatedSsh = [...(formData.ssh || [])]
                      if (updatedSsh.length === 0) {
                        updatedSsh.push({ name: "", host: "", port: 22, username: "", private_key: e.target.value })
                      } else {
                        updatedSsh[0].private_key = e.target.value
                      }
                      handleInputChange("ssh", updatedSsh)
                    }}
                    placeholder="Paste your private key here..."
                    rows={4}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="sshCommand">Command to Execute (Optional)</Label>
                  <Input
                    id="sshCommand"
                    value={formData.ssh?.[0]?.command || ""}
                    onChange={(e) => {
                      const updatedSsh = [...(formData.ssh || [])]
                      if (updatedSsh.length === 0) {
                        updatedSsh.push({ name: "", host: "", port: 22, username: "", command: e.target.value })
                      } else {
                        updatedSsh[0].command = e.target.value
                      }
                      handleInputChange("ssh", updatedSsh)
                    }}
                    placeholder="e.g., uptime"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        
        {/* Form Actions */}
        <div className="flex flex-col space-y-3 md:flex-row md:space-y-0 md:space-x-4 justify-end mt-8">
          <Button type="button" variant="outline" onClick={() => navigate(`/device/${id}`)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting} className="bg-black text-white hover:bg-black/90">
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Update Device
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="bg-red-600 text-white hover:bg-red-700"
            onClick={handleDelete}
            disabled={isDeleting || isSubmitting}
          >
            {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <X className="mr-2 h-4 w-4" />}
            Delete Device
          </Button>
        </div>
        {deleteError && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{deleteError}</AlertDescription>
          </Alert>
        )}

      </form>

      {submitStatus === "success" && (
        <Alert className="border-green-200 bg-green-50 mt-4">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Device has been updated successfully.
          </AlertDescription>
        </Alert>
      )}

      {submitStatus === "error" && (
        <Alert variant="destructive" className="mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{errors.submit || "Failed to update device. Please check your configuration and try again."}</AlertDescription>
        </Alert>
      )}
    </div>
  )
}
